"use client";

import { createContext, useContext } from "react";

export type WheelOnboardingStage = "review" | "find-deals" | "roll" | "covered-calls" | "results" | "suggested-actions" | "broker-connect" | "pricing" | "complete";

export const WHEEL_STAGES: WheelOnboardingStage[] = ["review", "find-deals", "roll", "covered-calls"];

export type GexOnboardingStage = "hub" | "gex-ai" | "heatmap" | "replay" | "gamma-flip" | "workspace";

// Default GEX tour (the "hub" stage is only injected when the `gexHub` feature
// flag is on — see GEX_STAGES_WITH_HUB and the flag-aware consumers).
export const GEX_STAGES: GexOnboardingStage[] = ["gex-ai", "heatmap", "replay", "gamma-flip", "workspace"];

// GEX tour with the Hub injected as step 2 (after GEX AI), used when the
// `gexHub` flag is enabled.
export const GEX_STAGES_WITH_HUB: GexOnboardingStage[] = [
  "gex-ai",
  "hub",
  "heatmap",
  "replay",
  "gamma-flip",
  "workspace",
];

export interface OnboardingModeContextType {
  isOnboardingMode: boolean;
  flow: "wheel" | "gex-first" | null;
  wheelStage: WheelOnboardingStage;
  completedStages: WheelOnboardingStage[];
  advanceStage: () => void;
  completeOnboarding: () => void;
  isQuickRecap: boolean;
  showBrokerConnect: boolean;
  setShowBrokerConnect: (show: boolean) => void;
  showPricing: boolean;
  setShowPricing: (show: boolean) => void;
  showIntro: boolean;
  dismissIntro: () => void;
  /** True for all non-paid users (persists after tour completion) */
  isFreeUser: boolean;
  /** True when user is on the GEX-only plan (paid but no full platform access) */
  isGexOnly: boolean;
  /** True for premium/special users who bypass onboarding/paywall restrictions */
  isSpecialUser: boolean;
  /** True once the subscription status API call has resolved (regardless of result) */
  subscriptionLoaded: boolean;
  /** True when the user has (or had) a Stripe subscription — not eligible for $1 trial CTA */
  hadPreviousSubscription: boolean;
  // GEX onboarding
  isGexOnboarding: boolean;
  gexStage: GexOnboardingStage;
  gexCompletedStages: GexOnboardingStage[];
  advanceGexStage: () => void;
  completeGexOnboarding: () => void;
  showGexIntro: boolean;
  dismissGexIntro: () => void;
  // Tour guard modal (shown instead of a paywall while onboarding is active)
  showTourGuard: boolean;
  setShowTourGuard: (show: boolean) => void;
  // Back navigation
  goBackStage: () => void;
  goBackGexStage: () => void;
  // Mid-tour switching (user picked the wrong tour in the starter)
  switchToGexTour: () => void;
  switchToWheelTour: () => void;
  // Tour completed flags (panels stay visible with Upgrade CTA)
  gexTourCompleted: boolean;
  wheelTourCompleted: boolean;
  /** True when user has at least one broker connected (real data available) */
  hasBrokerConnected: boolean;
  /** True when user is actively in a tour (not completed, not just free user) */
  isActivelyInTour: boolean;
}

export const OnboardingModeContext = createContext<OnboardingModeContextType>({
  isOnboardingMode: false,
  flow: null,
  wheelStage: "find-deals",
  completedStages: [],
  advanceStage: () => {},
  completeOnboarding: () => {},
  isQuickRecap: false,
  showBrokerConnect: false,
  setShowBrokerConnect: () => {},
  showPricing: false,
  setShowPricing: () => {},
  showIntro: false,
  dismissIntro: () => {},
  isFreeUser: false,
  isGexOnly: false,
  isSpecialUser: false,
  subscriptionLoaded: false,
  hadPreviousSubscription: false,
  isGexOnboarding: false,
  gexStage: "gex-ai",
  gexCompletedStages: [],
  advanceGexStage: () => {},
  completeGexOnboarding: () => {},
  showGexIntro: false,
  dismissGexIntro: () => {},
  showTourGuard: false,
  setShowTourGuard: () => {},
  goBackStage: () => {},
  goBackGexStage: () => {},
  switchToGexTour: () => {},
  switchToWheelTour: () => {},
  gexTourCompleted: false,
  wheelTourCompleted: false,
  hasBrokerConnected: false,
  isActivelyInTour: false,
});

export function useOnboardingMode() {
  return useContext(OnboardingModeContext);
}

export function stageIndex(stage: WheelOnboardingStage): number {
  return WHEEL_STAGES.indexOf(stage);
}

// Stages persisted by older tour versions that no longer exist in WHEEL_STAGES.
// Both were end-of-tour stages, so resume at the last current stage.
const LEGACY_WHEEL_STAGE_MAP: Record<string, WheelOnboardingStage> = {
  results: "covered-calls",
  "suggested-actions": "covered-calls",
};

/**
 * Restoring a persisted stage that is no longer part of WHEEL_STAGES traps the
 * user: stageHref can point at a route that redirects away (e.g. the legacy
 * "results" stage), so the tour path guard re-opens the exit modal forever,
 * and stageIndex(-1) locks every wheel tab. Always map persisted stages
 * through this before trusting them.
 */
export function sanitizeWheelStage(stage: unknown): WheelOnboardingStage {
  if (typeof stage === "string") {
    if ((WHEEL_STAGES as string[]).includes(stage)) return stage as WheelOnboardingStage;
    const mapped = LEGACY_WHEEL_STAGE_MAP[stage];
    if (mapped) return mapped;
  }
  return "review";
}

export function stageLabel(stage: WheelOnboardingStage): string {
  const labels: Record<string, string> = {
    review: "Active Wheels",
    "find-deals": "Find Deals",
    roll: "Roll",
    "covered-calls": "Covered Calls",
    "suggested-actions": "Suggested Actions",
  };
  return labels[stage] || stage;
}

export function stageHref(stage: WheelOnboardingStage): string {
  if (stage === "review") return "/dashboard/wheel/review";
  if (stage === "suggested-actions") return "/dashboard/wheel/review";
  return `/dashboard/wheel/${stage}`;
}

export function gexStageIndex(stage: GexOnboardingStage): number {
  return GEX_STAGES.indexOf(stage);
}

export function gexStageLabel(stage: GexOnboardingStage): string {
  const labels: Record<string, string> = {
    hub: "GEX Hub",
    "gex-ai": "GEX AI",
    heatmap: "GEX Heatmap",
    replay: "GEX Replay",
    "gamma-flip": "Gamma Flip",
    workspace: "Workspace",
  };
  return labels[stage] || stage;
}

export function gexStageHref(stage: GexOnboardingStage): string {
  const paths: Record<string, string> = {
    hub: "/dashboard/gex/hub",
    "gex-ai": "/dashboard/gex/gex-ai",
    heatmap: "/dashboard/gex/heatmap",
    replay: "/dashboard/gex/replay",
    "gamma-flip": "/dashboard/gex/gex-ai?tab=gamma-flip",
    workspace: "/dashboard/gex/workspace",
  };
  return paths[stage] || `/dashboard/gex/${stage}`;
}
